import { Calendar, Film, Sparkles, BarChart3 } from 'lucide-react';
import { LandingTemplate, type LandingData } from '../components/LandingTemplate';
import { LANDING_GUIDES } from '../lib/landingGuides';

const data: LandingData = {
  seo: {
    title: 'Cómo programar vídeos y Shorts en YouTube · Zettapost',
    description:
      'Cómo programar vídeos y Shorts en YouTube desde YouTube Studio y con Zettapost, coordinados con el resto de tus redes. Prueba 3 días gratis.',
    canonical: 'https://zettapost.com/programar-youtube',
  },
  hero: {
    eyebrow: 'Programar YouTube',
    headlinePre: 'Programa YouTube Shorts y vídeos',
    headlineAccent: 'desde una sola pestaña.',
    subhead:
      'Sube tus Shorts y vídeos largos, escribe la descripción con IA y programa la publicación a la hora exacta. Mismo flujo que para el resto de tus redes, sin abrir YouTube Studio.',
    bullets: [
      'Shorts y vídeos largos',
      'IA para título y descripción',
      'Métricas sincronizadas',
      '3 días gratis',
    ],
  },
  why: {
    eyebrow: '¿Por qué Zettapost para YouTube?',
    title: 'YouTube Studio sin el caos de YouTube Studio.',
    subtitle:
      'Pensado para creadores, marcas y agencias que publican vídeo en varios canales y quieren un solo sitio donde verlo todo.',
    items: [
      {
        Icon: Calendar,
        title: 'Programa cuando rinde',
        description:
          'Elige fecha y hora exacta de publicación de cada vídeo o Short. Zettapost se encarga de publicarlo, también de noche y los fines de semana.',
      },
      {
        Icon: Film,
        title: 'Shorts y vídeo largo',
        description:
          'Mismo editor para Shorts verticales (≤60 s) y vídeos largos. Subes el archivo, eliges la portada (thumbnail) y a programar.',
      },
      {
        Icon: Sparkles,
        title: 'IA para título y descripción',
        description:
          'La IA te propone título atractivo, descripción optimizada para SEO de YouTube y etiquetas relevantes. Tú revisas, ajustas y publicas.',
      },
      {
        Icon: BarChart3,
        title: 'Métricas en un panel',
        description:
          'Vistas, likes, comentarios y duración media de cada vídeo sincronizadas desde YouTube. Compara qué temas y formatos te están funcionando.',
      },
    ],
  },
  guide: LANDING_GUIDES.youtube,
  faqs: [
    {
      q: '¿Puedo programar Shorts desde el ordenador?',
      a: 'Sí. Subes el archivo vertical 9:16 (hasta 60 segundos), eliges la portada y la hora y Zettapost lo programa como Short nativo. Se publica con la API oficial de YouTube, sin marca de agua.',
    },
    {
      q: '¿Y vídeos largos también?',
      a: 'Sí. Soportamos vídeos largos hasta el límite de subida de tu canal de YouTube. Mismo flujo: archivo, título, descripción, portada, fecha y hora de publicación.',
    },
    {
      q: '¿Puedo cambiar la portada (thumbnail)?',
      a: 'Sí. Subes una imagen personalizada como portada al programar el vídeo. Si no subes ninguna, YouTube usa un frame del vídeo automáticamente.',
    },
    {
      q: '¿Puedo conectar varios canales?',
      a: 'Sí. Los planes con varios canales permiten gestionar varios canales de YouTube desde el mismo panel, separados por marca. Ideal para creadores con varios canales o agencias.',
    },
    {
      q: '¿Soporta playlists y capítulos?',
      a: 'Las playlists se gestionan desde YouTube Studio una vez publicado el vídeo. Los capítulos sí: se añaden desde la descripción, en el formato estándar de YouTube (minuto + título).',
    },
    {
      q: '¿La cuenta de prueba programa vídeos?',
      a: 'Sí. Durante los 3 días de prueba gratuita puedes programar todos los vídeos y Shorts que quieras, sin restricciones.',
    },
  ],
  finalCta: {
    title: 'Programa tu próximo Short',
    titleAccent: 'sin abrir YouTube Studio.',
    description: 'Crea tu cuenta y conecta YouTube gratis durante 3 días.',
  },
  related: [
    { to: '/programar-tiktok', label: 'Programar TikTok' },
    { to: '/programar-instagram', label: 'Programar Instagram' },
    { to: '/precios', label: 'Ver precios' },
    { to: '/blog', label: 'Blog' },
  ],
};

export function ProgramarYoutube() {
  return <LandingTemplate data={data} />;
}
